import React, { useState } from 'react';
import { 
  Folder, 
  FolderPlus, 
  Edit, 
  Trash2, 
  ChevronRight, 
  ChevronDown,
  FileText,
  Plus,
  Search
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

interface FolderNode {
  id: string;
  name: string;
  type: 'folder' | 'note';
  parentId?: string;
  children?: FolderNode[];
  isExpanded?: boolean;
  noteCount?: number;
}

interface FolderManagerProps {
  onSelectFolder: (folderId: string | null) => void;
  selectedFolderId: string | null;
  onCreateNote: (folderId?: string) => void;
}

const FolderManager: React.FC<FolderManagerProps> = ({ 
  onSelectFolder, 
  selectedFolderId, 
  onCreateNote 
}) => {
  const { colors } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParent, setNewFolderParent] = useState<string | null>(null);
  const [editingFolder, setEditingFolder] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const [folders, setFolders] = useState<FolderNode[]>([
    {
      id: 'root',
      name: 'All Notes',
      type: 'folder',
      isExpanded: true,
      noteCount: 12,
      children: [
        {
          id: 'programming',
          name: 'Programming',
          type: 'folder',
          parentId: 'root',
          isExpanded: true,
          noteCount: 8,
          children: [
            {
              id: 'react',
              name: 'React',
              type: 'folder',
              parentId: 'programming',
              isExpanded: false,
              noteCount: 5,
              children: [
                { id: 'note1', name: 'React Fundamentals', type: 'note', parentId: 'react' },
                { id: 'note2', name: 'React Hooks', type: 'note', parentId: 'react' },
                { id: 'note3', name: 'Component Patterns', type: 'note', parentId: 'react' },
                { id: 'note4', name: 'State Management', type: 'note', parentId: 'react' },
                { id: 'note5', name: 'Performance Tips', type: 'note', parentId: 'react' }
              ]
            },
            {
              id: 'javascript',
              name: 'JavaScript',
              type: 'folder',
              parentId: 'programming',
              isExpanded: false,
              noteCount: 3,
              children: [
                { id: 'note6', name: 'ES6 Features', type: 'note', parentId: 'javascript' },
                { id: 'note7', name: 'Async Programming', type: 'note', parentId: 'javascript' },
                { id: 'note8', name: 'DOM Manipulation', type: 'note', parentId: 'javascript' }
              ]
            }
          ]
        },
        {
          id: 'design',
          name: 'Design',
          type: 'folder',
          parentId: 'root',
          isExpanded: false,
          noteCount: 4,
          children: [
            { id: 'note9', name: 'UI/UX Principles', type: 'note', parentId: 'design' },
            { id: 'note10', name: 'Color Theory', type: 'note', parentId: 'design' },
            { id: 'note11', name: 'Typography', type: 'note', parentId: 'design' },
            { id: 'note12', name: 'Design Systems', type: 'note', parentId: 'design' }
          ]
        }
      ]
    }
  ]);

  const toggleFolder = (folderId: string) => {
    const updateFolders = (nodes: FolderNode[]): FolderNode[] => {
      return nodes.map(node => {
        if (node.id === folderId) {
          return { ...node, isExpanded: !node.isExpanded };
        }
        if (node.children) {
          return { ...node, children: updateFolders(node.children) };
        }
        return node;
      });
    };
    setFolders(updateFolders(folders));
  };

  const handleCreateFolder = () => {
    if (newFolderName.trim()) {
      const newFolder: FolderNode = {
        id: Date.now().toString(),
        name: newFolderName.trim(),
        type: 'folder',
        parentId: newFolderParent || 'root',
        isExpanded: false,
        noteCount: 0,
        children: []
      };

      const addToParent = (nodes: FolderNode[]): FolderNode[] => {
        return nodes.map(node => {
          if (node.id === (newFolderParent || 'root')) {
            return {
              ...node,
              children: [...(node.children || []), newFolder]
            };
          }
          if (node.children) {
            return { ...node, children: addToParent(node.children) };
          }
          return node;
        });
      };

      setFolders(addToParent(folders));
      setNewFolderName('');
      setNewFolderParent(null);
      setIsCreatingFolder(false);
    }
  };

  const handleEditFolder = (folderId: string, newName: string) => {
    if (newName.trim()) {
      const updateFolders = (nodes: FolderNode[]): FolderNode[] => {
        return nodes.map(node => {
          if (node.id === folderId) {
            return { ...node, name: newName.trim() };
          }
          if (node.children) {
            return { ...node, children: updateFolders(node.children) };
          }
          return node;
        });
      };
      setFolders(updateFolders(folders));
    }
    setEditingFolder(null);
    setEditName('');
  };

  const handleDeleteFolder = (folderId: string) => {
    if (confirm('Are you sure you want to delete this folder and all its contents?')) {
      const deleteFolders = (nodes: FolderNode[]): FolderNode[] => {
        return nodes.filter(node => node.id !== folderId).map(node => {
          if (node.children) {
            return { ...node, children: deleteFolders(node.children) };
          }
          return node;
        });
      };
      setFolders(deleteFolders(folders));
      if (selectedFolderId === folderId) {
        onSelectFolder(null);
      }
    }
  };

  const renderFolderTree = (nodes: FolderNode[], level: number = 0): React.ReactNode => {
    return nodes.map(node => {
      const isSelected = selectedFolderId === node.id;
      const hasChildren = node.children && node.children.length > 0;
      const isEditing = editingFolder === node.id;

      if (node.type === 'note') {
        return (
          <div
            key={node.id}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors hover:opacity-80 ${
              isSelected ? 'text-white' : ''
            }`}
            style={{
              marginLeft: `${(level + 1) * 20}px`,
              backgroundColor: isSelected ? colors.blue : 'transparent',
              color: isSelected ? 'white' : colors.text
            }}
            onClick={() => onSelectFolder(node.id)}
          >
            <FileText className="w-4 h-4" />
            <span className="text-sm">{node.name}</span>
          </div>
        );
      }

      return (
        <div key={node.id}>
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors hover:opacity-80 group ${
              isSelected ? 'text-white' : ''
            }`}
            style={{
              marginLeft: `${level * 20}px`,
              backgroundColor: isSelected ? colors.blue : 'transparent',
              color: isSelected ? 'white' : colors.text
            }}
          >
            {hasChildren && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolder(node.id);
                }}
                className="p-1 rounded hover:opacity-80"
              >
                {node.isExpanded ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
              </button>
            )}
            
            {!hasChildren && <div className="w-5" />}
            
            <Folder className="w-4 h-4" />
            
            {isEditing ? (
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onBlur={() => handleEditFolder(node.id, editName)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleEditFolder(node.id, editName);
                  } else if (e.key === 'Escape') {
                    setEditingFolder(null);
                    setEditName('');
                  }
                }}
                className="flex-1 text-sm bg-transparent border-b border-current outline-none"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span 
                className="flex-1 text-sm"
                onClick={() => onSelectFolder(node.id)}
              >
                {node.name}
              </span>
            )}
            
            {node.noteCount !== undefined && (
              <span className="text-xs opacity-60">
                {node.noteCount}
              </span>
            )}
            
            {node.id !== 'root' && (
              <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateNote(node.id);
                  }}
                  className="p-1 rounded hover:opacity-80"
                  title="Add note to folder"
                >
                  <Plus className="w-3 h-3" />
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingFolder(node.id);
                    setEditName(node.name);
                  }}
                  className="p-1 rounded hover:opacity-80"
                  title="Rename folder"
                >
                  <Edit className="w-3 h-3" />
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteFolder(node.id);
                  }}
                  className="p-1 rounded hover:opacity-80"
                  title="Delete folder"
                  style={{ color: colors.red }}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
          
          {node.isExpanded && hasChildren && (
            <div>
              {renderFolderTree(node.children!, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div 
      className="w-80 border-r flex flex-col"
      style={{ 
        backgroundColor: colors.mantle,
        borderColor: colors.surface0
      }}
    >
      {/* Header */}
      <div className="p-4 border-b" style={{ borderColor: colors.surface0 }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold" style={{ color: colors.text }}>
            Folders
          </h3>
          
          <button
            onClick={() => {
              setIsCreatingFolder(true);
              setNewFolderParent(selectedFolderId);
            }}
            className="p-1 rounded hover:opacity-80"
            style={{ color: colors.blue }}
            title="Create new folder"
          >
            <FolderPlus className="w-4 h-4" />
          </button>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search 
            className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4"
            style={{ color: colors.subtext0 }}
          />
          <input
            type="text"
            placeholder="Search folders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm rounded border focus:outline-none focus:ring-1"
            style={{
              backgroundColor: colors.base,
              borderColor: colors.surface1,
              color: colors.text
            }}
          />
        </div>
      </div>

      {/* Create Folder Form */}
      {isCreatingFolder && (
        <div 
          className="p-4 border-b"
          style={{ 
            backgroundColor: colors.surface0,
            borderColor: colors.surface1
          }}
        >
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Folder name..."
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded border focus:outline-none focus:ring-1"
              style={{
                backgroundColor: colors.base,
                borderColor: colors.surface1,
                color: colors.text
              }}
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCreateFolder();
                } else if (e.key === 'Escape') {
                  setIsCreatingFolder(false);
                  setNewFolderName('');
                  setNewFolderParent(null);
                }
              }}
            />
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleCreateFolder}
                className="px-3 py-1 text-sm rounded font-medium text-white"
                style={{ backgroundColor: colors.green }}
              >
                Create
              </button>
              
              <button
                onClick={() => {
                  setIsCreatingFolder(false);
                  setNewFolderName('');
                  setNewFolderParent(null);
                }}
                className="px-3 py-1 text-sm rounded border"
                style={{ 
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  color: colors.text
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Folder Tree */}
      <div className="flex-1 overflow-y-auto p-2">
        <div className="space-y-1">
          {renderFolderTree(folders)}
        </div>
      </div>
    </div>
  );
};

export default FolderManager;